export type UiLanguage = 'pt-BR' | 'en' | 'es'

export const DEFAULT_UI_LANGUAGE: UiLanguage = 'pt-BR'

export const SUPPORTED_UI_LANGUAGES: readonly UiLanguage[] = ['pt-BR', 'en', 'es']

/** Same convention as `rolt_user_session` in utils/sessionStorage.ts. */
export const UI_LANGUAGE_STORAGE_KEY = 'rolt_ui_language'

export const UI_LANGUAGE_OPTIONS: ReadonlyArray<{
  value: UiLanguage
  label: string
}> = [
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
]

export function isUiLanguage(value: unknown): value is UiLanguage {
  return value === 'pt-BR' || value === 'en' || value === 'es'
}

/** Maps any BCP-47 tag onto a supported language: `pt`, `pt-PT` → pt-BR; `en-US` → en. */
export function matchUiLanguage(tag: unknown): UiLanguage | null {
  if (typeof tag !== 'string' || !tag) return null
  const normalized = tag.toLowerCase()
  if (normalized.startsWith('pt')) return 'pt-BR'
  if (normalized.startsWith('en')) return 'en'
  if (normalized.startsWith('es')) return 'es'
  return null
}

export function readStoredUiLanguage(): UiLanguage | null {
  try {
    return isUiLanguage(localStorage.getItem(UI_LANGUAGE_STORAGE_KEY))
      ? (localStorage.getItem(UI_LANGUAGE_STORAGE_KEY) as UiLanguage)
      : null
  } catch {
    // Private mode / storage disabled — not worth failing the boot over.
    return null
  }
}

export function storeUiLanguage(value: UiLanguage): void {
  try {
    localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, value)
  } catch {
    /* ignore */
  }
}

/**
 * Language to boot with, before any account is loaded.
 *
 * Public screens (login, password recovery, privacy) never see `accounts.ui_language`, so the
 * last language the user actually used wins, then the browser's, then pt-BR.
 */
export function resolveBootstrapLanguage(): UiLanguage {
  const stored = readStoredUiLanguage()
  if (stored) return stored

  try {
    const tags = navigator.languages?.length ? navigator.languages : [navigator.language]
    for (const tag of tags) {
      const matched = matchUiLanguage(tag)
      if (matched) return matched
    }
  } catch {
    /* ignore */
  }

  return DEFAULT_UI_LANGUAGE
}
